import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import googleApi from '../services/googleApi';
import { formatDateTime, timeAgo } from '../utils/time';
import Icon from './Icon';
import './RelationshipManagers.css';
import './AdminPanel.css';
import './MeetingsSection.css';

/**
 * The signed-in person's Google account: what colleagues will see once they connect, the Connect
 * button (which is also their agreement), and Disconnect. `notice` is the outcome of a Connect
 * the browser just came back from, if any.
 */
const GoogleAccountDialog = ({ isOpen, onClose, notice }) => {
  const [info, setInfo] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setInfo(null);
    setError(null);
    setAgreed(false);
    googleApi.mine().then(data => !cancelled && setInfo(data)).catch(err => !cancelled && setError(err.message));
    dialogRef.current?.focus();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorization_url: url } = await googleApi.connect();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Google account? Sinatra stops reading your calendar.')) return;
    setBusy(true);
    setError(null);
    try {
      await googleApi.disconnect();
      setInfo(await googleApi.mine());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const connection = info?.connection;
  const live = connection && connection.status !== 'revoked';

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog" role="dialog" aria-modal="true" aria-labelledby="google-title" ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <div>
            <h2 id="google-title"><Icon name="calendar" /> Google account</h2>
            <p>Lets Sinatra show your meetings and email history with clients and partners in each company's panel.</p>
          </div>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </header>
        <div className="rm-dialog__body adm-tab">
          {notice && (
            <p className={notice.error ? 'company-panel-error' : 'google-status'} role={notice.error ? 'alert' : 'status'}>
              <Icon name={notice.error ? 'alert-triangle' : 'check'} /> {notice.message}
            </p>
          )}
          {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
          {!info && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}

          {info && !info.configured && (
            <p className="placeholder-text">
              Connecting Google isn't set up yet ({info.missing.join(', ')} missing). Ask an admin.
            </p>
          )}

          {info && info.configured && live && (
            <>
              <div className={`google-status${connection.status === 'error' ? ' google-status--error' : ''}`}>
                <strong>{connection.google_email}</strong>
                <span>{connection.status === 'error' ? connection.error_note : 'Connected'}</span>
                <span className="meet__meta">
                  {connection.calendar_synced_at
                    ? <>Last synced <span title={formatDateTime(connection.calendar_synced_at)}>{timeAgo(connection.calendar_synced_at)}</span></>
                    : 'Not synced yet'}
                </span>
              </div>
              {!connection.calendar && (
                <p className="placeholder-text">Calendar access wasn't allowed, so no meetings are read. Disconnect and connect again to allow it.</p>
              )}
              {connection.calendar && !connection.gmail && (
                <p className="placeholder-text">Email access wasn't allowed, so no email history is read. Disconnect and connect again to allow it.</p>
              )}
              <div className="rm-buttons">
                <button type="button" className="rm-button" onClick={disconnect} disabled={busy}>Disconnect</button>
                {connection.status === 'error' && (
                  <button type="button" className="rm-button rm-button--primary" onClick={connect} disabled={busy}>Reconnect</button>
                )}
              </div>
            </>
          )}

          {info && info.configured && !live && (
            <>
              <div className="google-notice">
                <h3>{info.notice.title}</h3>
                <p>{info.notice.intro}</p>
                <ul>{info.notice.items.map(item => <li key={item}>{item}</li>)}</ul>
                <p>{info.notice.outro}</p>
              </div>
              {info.can_connect ? (
                <>
                  <label className="rm-picker__option">
                    <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} disabled={busy} />
                    <span className="rm-picker__label">I understand, and I agree to colleagues seeing this</span>
                  </label>
                  <p className="meet__meta">Sinatra reads meetings from {new Date(`${info.sync_from}T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}. Only members of the sales group can complete this in Google.</p>
                  <div className="rm-buttons">
                    <button type="button" className="rm-button rm-button--primary" onClick={connect} disabled={!agreed || busy}>
                      <Icon name={busy ? 'loader' : 'calendar'} spin={busy} /> Connect Google
                    </button>
                  </div>
                </>
              ) : (
                <p className="placeholder-text">Ask an admin to add you to the sales group before connecting.</p>
              )}
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default GoogleAccountDialog;
